// services/auth/RateLimiter.ts
// Rate Limiting на клиенте для защиты от spam

import { consola } from 'consola'

// Logger
const logger = consola.withTag('rate-limiter')

/**
 * Конфигурация rate limiter
 */
export interface RateLimiterConfig {
  maxAttempts: number      // Максимум попыток
  windowMs: number         // Временное окно (мс)
  blockDurationMs: number  // Длительность блокировки (мс)
}

/**
 * Попытка действия
 */
interface Attempt {
  timestamp: number
  action: string
}

/**
 * RateLimiter - ограничение частоты действий
 * 
 * Решает проблемы:
 * - Спам кнопки "Войти"
 * - Brute force атаки
 * - DDos самого себя
 * - Перегрузка API
 */
export class RateLimiter {
  private attempts: Map<string, Attempt[]> = new Map()
  private blockedUntil: Map<string, number> = new Map()
  
  private defaultConfig: RateLimiterConfig = {
    maxAttempts: 5,
    windowMs: 60_000,        // 1 минута
    blockDurationMs: 300_000, // 5 минут
  }
  
  // Конфигурации для разных действий
  private configs: Map<string, RateLimiterConfig> = new Map([
    ['login', { maxAttempts: 3, windowMs: 60_000, blockDurationMs: 180_000 }],        // 3 попытки в минуту, блок на 3 мин
    ['register', { maxAttempts: 2, windowMs: 300_000, blockDurationMs: 600_000 }],    // 2 попытки в 5 мин, блок на 10 мин
    ['password_reset', { maxAttempts: 3, windowMs: 300_000, blockDurationMs: 600_000 }], // 3 попытки в 5 мин
    ['refresh_token', { maxAttempts: 10, windowMs: 60_000, blockDurationMs: 60_000 }],   // 10 попыток в минуту
  ])
  
  constructor() {
    logger.info('Инициализация')
    
    // Очистка старых попыток каждые 5 минут
    if (process.client) {
      setInterval(() => this.cleanup(), 300_000)
    }
  }
  
  /**
   * Проверить, можно ли выполнить действие
   */
  public canAttempt(action: string): boolean {
    const now = Date.now()
    
    // Проверяем, не заблокирован ли
    const blockedUntil = this.blockedUntil.get(action)
    /* v8 ignore next */
    if (blockedUntil && now < blockedUntil) {
      /* v8 ignore next 4 */
      const remainingMs = blockedUntil - now
      logger.warn(`Действие "${action}" заблокировано еще на ${Math.ceil(remainingMs / 1000)} сек`)
      return false
    }
    
    // Убираем блокировку если время истекло
    if (blockedUntil && now >= blockedUntil) {
      this.blockedUntil.delete(action)
      this.attempts.delete(action)
    }
    
    // Получаем конфигурацию
    const config = this.configs.get(action) || this.defaultConfig
    
    // Получаем попытки в текущем окне
    const recentAttempts = this.getRecentAttempts(action, config.windowMs)
    
    // Проверяем лимит
    if (recentAttempts.length >= config.maxAttempts) {
      logger.warn(`Превышен лимит для "${action}" (${recentAttempts.length}/${config.maxAttempts})`)
      
      // Блокируем на определенное время
      this.block(action, config.blockDurationMs)
      
      return false
    }
    
    return true
  }
  
  /**
   * Записать попытку
   */
  public recordAttempt(action: string): void {
    const attempts = this.attempts.get(action) || []
    attempts.push({
      timestamp: Date.now(),
      action,
    })
    this.attempts.set(action, attempts)
    
    const config = this.configs.get(action) || this.defaultConfig
    const recentAttempts = this.getRecentAttempts(action, config.windowMs)
    
    logger.info(`Попытка "${action}" (${recentAttempts.length}/${config.maxAttempts})`)
  }
  
  /**
   * Попытка выполнить действие
   */
  public async attempt<T>(
    action: string,
    fn: () => Promise<T>,
    onBlocked?: (remainingMs: number) => void,
  ): Promise<T> {
    // Проверяем лимит
    if (!this.canAttempt(action)) {
      const remainingMs = this.getBlockedRemainingTime(action)
      
      if (onBlocked) {
        onBlocked(remainingMs)
      }
      
      throw new Error(`Rate limit exceeded for "${action}". Try again in ${Math.ceil(remainingMs / 1000)} seconds`)
    }
    
    // Записываем попытку
    this.recordAttempt(action)
    
    // Выполняем функцию
    const result = await fn()
    
    // Успех - сбрасываем счетчик для этого действия
    this.reset(action)
    
    return result
  }
  
  /**
   * Заблокировать действие
   */
  private block(action: string, durationMs: number): void {
    const blockedUntil = Date.now() + durationMs
    this.blockedUntil.set(action, blockedUntil)
    
    logger.warn(`"${action}" заблокирован до ${new Date(blockedUntil).toLocaleTimeString()}`)
  }
  
  /**
   * Получить недавние попытки
   */
  private getRecentAttempts(action: string, windowMs: number): Attempt[] {
    const attempts = this.attempts.get(action) || []
    const cutoff = Date.now() - windowMs
    return attempts.filter(a => a.timestamp > cutoff)
  }
  
  /**
   * Получить оставшееся время блокировки
   */
  public getBlockedRemainingTime(action: string): number {
    const blockedUntil = this.blockedUntil.get(action)
    if (!blockedUntil) return 0
    
    const remaining = blockedUntil - Date.now()
    return Math.max(0, remaining)
  }
  
  /**
   * Проверить, заблокировано ли действие
   */
  public isBlocked(action: string): boolean {
    return this.getBlockedRemainingTime(action) > 0
  }
  
  /**
   * Сбросить счетчик для действия
   */
  public reset(action: string): void {
    this.attempts.delete(action)
    this.blockedUntil.delete(action)
    logger.info(`Счетчик для "${action}" сброшен`)
  }
  
  /**
   * Сбросить все
   */
  public resetAll(): void {
    this.attempts.clear()
    this.blockedUntil.clear()
    logger.info('Все счетчики сброшены')
  }
  
  /**
   * Очистить старые попытки
   */
  private cleanup(): void {
    const now = Date.now()
    let cleaned = 0
    
    for (const [action, attempts] of this.attempts.entries()) {
      const config = this.configs.get(action) || this.defaultConfig
      const cutoff = now - config.windowMs * 2 // Удаляем попытки старше 2x окна
      
      const filtered = attempts.filter(a => a.timestamp > cutoff)
      
      if (filtered.length === 0) {
        this.attempts.delete(action)
        cleaned++
      /* v8 ignore next */
      } else if (filtered.length < attempts.length) {
        /* v8 ignore next 2 */
        this.attempts.set(action, filtered)
      }
    }
    
    if (cleaned > 0) {
      logger.info(`Очищено ${cleaned} старых записей`)
    }
  }
  
  /**
   * Получить статистику
   */
  public getStats(): {
    totalAttempts: number
    blockedActions: string[]
    attemptsPerAction: Record<string, number>
  } {
    const blockedActions: string[] = []
    const attemptsPerAction: Record<string, number> = {}
    
    for (const [action, until] of this.blockedUntil.entries()) {
      if (until > Date.now()) {
        blockedActions.push(action)
      }
    }
    
    for (const [action, attempts] of this.attempts.entries()) {
      attemptsPerAction[action] = attempts.length
    }
    
    return {
      totalAttempts: [...this.attempts.values()].reduce((sum, arr) => sum + arr.length, 0),
      blockedActions,
      attemptsPerAction,
    }
  }
  
  /**
   * Установить кастомную конфигурацию для действия
   */
  public setConfig(action: string, config: RateLimiterConfig): void {
    this.configs.set(action, config)
    logger.info(`Конфигурация для "${action}" обновлена`, config)
  }
}

// Экспортируем единственный экземпляр
export const rateLimiter = new RateLimiter()

